/**
 * 보호된 라우트 컴포넌트
 *
 * 주요 기능:
 * - 인증이 필요한 페이지에 대한 접근 제어
 * - 토큰 기반 사용자 인증 상태 확인
 * - 로그인하지 않은 사용자를 홈페이지로 리다이렉트
 * - 토큰이 있을 경우 자동으로 사용자 프로필 복원
 *
 * 동작 흐름:
 * 1. 액세스 토큰 존재 여부 확인
 * 2. 토큰이 있으면 사용자 프로필 조회 및 복원
 * 3. 토큰이 없거나 유효하지 않으면 홈페이지로 리다이렉트
 * 4. 인증 확인 중에는 로딩 상태 표시
 *
 * 사용처: router/index.tsx에서 인증이 필요한 라우트를 감싸는 용도
 */

import { logger } from "@/utils/logger";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { tokenService } from "@/utils/tokenService";
import { useEffect, useState } from "react";
import { getProfile } from "@/services/auth/userService";
import { LoadingState } from "../common/LoadingState";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Protected Route 컴포넌트
 *
 * 인증이 필요한 페이지를 보호하고, 미인증 사용자를 리다이렉트 처리
 *
 * @param children - 보호할 자식 컴포넌트
 * @returns 인증된 경우 자식 컴포넌트, 미인증 시 홈으로 리다이렉트
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, setUser, clearUser, isProfileFetching, isProfileFetched } =
    useAuthStore();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    /**
     * 인증 상태 확인 및 사용자 세션 복원
     *
     * 1. 액세스 토큰 확인
     * 2. 토큰이 없으면 인증 상태 초기화
     * 3. 사용자 정보가 없으면 프로필 API 호출하여 복원
     * 4. API 실패 시 토큰 및 사용자 정보 초기화
     */
    const checkAuth = async () => {
      const accessToken = tokenService.getAccessToken();

      // 토큰이 없는 경우 - 인증 상태 초기화
      if (!accessToken) {
        if (user) {
          clearUser();
        }
        setIsChecking(false);
        return;
      }

      // 사용자 정보가 이미 로드되었거나 로딩 중인 경우 - 건너뛰기
      if (user || isProfileFetching) {
        setIsChecking(false);
        return;
      }

      // 토큰은 있지만 사용자 정보가 없는 경우 - 프로필 조회
      if (!user) {
        try {
          const res = await getProfile();
          if (res.success && res.data) {
            setUser(res.data);
          }
        } catch (error) {
          logger.error("Failed to restore user from token:", { error });
          // 토큰이 유효하지 않음 - 모든 정보 초기화
          clearUser();
          tokenService.removeTokens();
        }
      }

      setIsChecking(false);
    };

    checkAuth();
  }, [user, setUser, clearUser, isProfileFetching, isProfileFetched]);

  const accessToken = tokenService.getAccessToken();

  // 인증 확인 중 로딩 상태 표시
  if (isChecking) {
    return <LoadingState />;
  }

  // 토큰 또는 사용자 정보가 없는 경우 - 홈으로 리다이렉트
  if (!accessToken || !user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
